import { ContentTags } from '@/postElement/SerializerV2/components/ContentTags';
import { PaidContent } from '@/postElement/SerializerV2/components/PaidContent';
import type { SerializableNode, SerializerProps } from '@/serializer';
import type { Breadcrumb, PostHeaderData, UTMAttrs } from '@/types';
import { formatPostTime, updateText } from '@/utils/post/utils';
import type { JSONContent } from '@tiptap/core';
import { produce } from 'immer';
import type { ReactNode } from 'react';
import { PostAuthors } from '../../postElement/SerializerV2/components/PostAuthors';
import { getElement } from '../../serializer/getElement';
import { SerializeTextLeaf, type TTextLeaf } from '../../textLeaf';
interface ExtendedSerializerProps extends SerializerProps {
  post: PostHeaderData;
  breadcrumbs: Breadcrumb[];
  node: JSONContent & SerializableNode;
  shareUrl: string;
  isLive?: boolean;
  utmParams?: UTMAttrs;
}

// For draft posts, when post.estimated_reading_time is null
const FALLBACK_READ_TIME_MINUTES = 2;

const serializeNode = (
  {
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    createKey = (entry) => entry[1].join(','),
    children,
    post,
    breadcrumbs,
    isLive,
    utmParams,
    shareUrl,
  }: ExtendedSerializerProps,
  path = [0], // internal path tracking
): ReactNode => {
  if (!node) {
    return null;
  }

  const key = createKey([node, path]);

  const isImage = node.type === 'image';
  const isParagraph = node.type === 'paragraph';
  const isHeading = node.type === 'heading';
  const isBreadcrumbs = node.type === 'breadcrumbs';
  const isShareElement = node.type === 'postShare';

  if (isShareElement) {
    node = produce(node, (draft) => {
      if (draft.attrs) {
        draft.attrs.data = {
          title: post.web_title,
          url: shareUrl,
        };
      } else {
        draft.attrs = { data: { title: post.web_title, url: shareUrl } };
      }
    });
  }

  // Only show paid content if the post is premium
  if (node.attrs?.mergeTag === 'post.paidContent' && !post.is_premium) {
    return null;
  }

  // Pass breadcrumbs
  if (isBreadcrumbs) {
    node = produce(node, (draft) => {
      if (draft.attrs) {
        draft.attrs.data = { breadcrumbs };
      } else {
        draft.attrs = { data: { breadcrumbs } };
      }
    });
  }

  // Swap out images
  if (isImage) {
    if (node.attrs?.mergeTag !== 'post.image') {
      return null;
    }

    if (post.display_thumbnail_on_web !== true) {
      return null;
    }

    node = produce(node, (draft) => {
      if (!draft.attrs) {
        draft.attrs = {};
      }

      draft.attrs.src = post?.image_url;
      draft.attrs.aspectRatio = undefined;
    });
  }

  // Swap out headings && paragraphs
  if ((isParagraph || isHeading) && node.attrs?.mergeTag) {
    if (node.attrs.mergeTag === 'post.title') {
      node = updateText(node, post?.web_title) as JSONContent & SerializableNode;
    }
    if (node.attrs?.mergeTag === 'post.subtitle') {
      if (!post?.web_subtitle) {
        return null;
      }

      node = updateText(node, post.web_subtitle) as JSONContent & SerializableNode;
    }
    if (node.attrs?.mergeTag === 'post.timestamp') {
      node = updateText(
        node,
        formatPostTime(post?.override_scheduled_at, node.attrs?.postTimestampDisplay),
      ) as JSONContent & SerializableNode;
    }
    if (node.attrs?.mergeTag === 'post.readTime') {
      node = updateText(
        node,
        `${post?.estimated_reading_time || FALLBACK_READ_TIME_MINUTES} ${node.attrs?.postReadTimeSuffix}`,
      ) as JSONContent & SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'post.authors') {
      if (Array.isArray(post?.authors) && post.authors.length === 0) {
        return null;
      }

      return (
        <PostAuthors
          authors={post?.authors || []}
          authorUrls={post?.author_urls}
          node={node}
          utmParams={utmParams}
          key={key}
        />
      );
    }
    if (node?.attrs?.mergeTag === 'post.tags') {
      return <ContentTags tags={post?.content_tags || []} node={node} key={key} />;
    }
    if (node?.attrs?.mergeTag === 'post.paidContent') {
      return <PaidContent node={node} isPremium={!post.is_premium || false} key={key} />;
    }
  }

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} />;
  }

  const nodeChildren =
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
          isImageOptimizable,
          post,
          breadcrumbs,
          utmParams,
          shareUrl,
        },
        [...path, i],
      ),
    ) || [];

  return getElement({
    node: node as SerializableNode,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    key,
    children: [...nodeChildren, children],
    isLive,
  });
};

export function Serializer(props: ExtendedSerializerProps) {
  return <>{serializeNode(props)}</>;
}
